import type { ReportSpec } from '../models/report.types'

/**
 * Fields that change how much of a result set a request reads, never what the controller groups
 * or filters by — plus `metrics`, which `buildReportQuery` no longer serializes at all.
 */
const NON_QUESTION_FIELDS = new Set<keyof ReportSpec>([
  'first',
  'rows',
  'pagination_enabled',
  'metrics',
])

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical)
  if (typeof value !== 'object' || value === null) return value
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, canonical(entry)]),
  )
}

/**
 * A stable key for the *question* a spec asks the controller, so two renderers of one surface can
 * tell "same read, share the response" from "different read, ask twice". Key order and the paging
 * window are normalised away; everything that selects or shapes rows is kept.
 *
 * A null spec (nothing to ask) signs as the empty string, which makes an unavailable chart share
 * an unavailable table rather than open a request of its own.
 */
export function requestSignature(spec: ReportSpec | null): string {
  if (!spec) return ''
  const question = Object.fromEntries(
    Object.entries(spec).filter(([key]) => !NON_QUESTION_FIELDS.has(key as keyof ReportSpec)),
  )
  return JSON.stringify(canonical(question))
}
